import { useState, useEffect } from 'react';
import { message } from 'antd';
import { Add, ArrowDown2, ArrowRight2, CloseSquare, Edit2, Trash } from 'iconsax-react';
import InfoText from '../../../../components/atoms/InfoText';
import SubCategoryRow from './SubCategoryRow';
import { Category, SubCategory } from '../../../../types/lookups';
import { useLazyGetOperatorCategorySpecialitiesQuery, useDeleteOperatorCategoryMutation } from '../../../../store/services/lookups';
import { useTranslation } from 'react-i18next';
import CustomModal from '../../../../components/atoms/CustomModal';
import TitleText from '../../../../components/atoms/TitleText';
import Button from '../../../../components/atoms/Button';

interface CategoryRowProps {
  category: Category;
  onEditCategory: () => void;
  onAddSubCategory: () => void;
  onEditSubCategory: (subCat: SubCategory) => void;
}

export default function CategoryRow({
  category,
  onEditCategory,
  onAddSubCategory,
  onEditSubCategory,
}: CategoryRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [subCategories, setSubCategories] = useState<SubCategory[] | null>(null);
  const [isLoadingSubCategories, setIsLoadingSubCategories] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { t } = useTranslation();
  const [fetchSpecialities] = useLazyGetOperatorCategorySpecialitiesQuery();
  const [deleteOperatorCategory, { isLoading: isDeleting }] = useDeleteOperatorCategoryMutation();

  // Fetch subcategories the first time the row is expanded
  useEffect(() => {
    if (!isExpanded || subCategories !== null) return;

    setIsLoadingSubCategories(true);
    fetchSpecialities({ categoryId: category.id })
      .unwrap()
      .then((data) => setSubCategories(data))
      .catch((error) => console.error('Failed to fetch specialities', error))
      .finally(() => setIsLoadingSubCategories(false));
  }, [isExpanded, category.id, subCategories, fetchSpecialities]);

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-150 shadow-sm overflow-hidden">
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ArrowDown2 size={18} variant='Bold' />
            ) : (
              <ArrowRight2 size={18} variant='Bold' />
            )}
             <img
              src={category.iconUrl || 'https://via.placeholder.com/40'}
              alt={category.nameEn}
              className="w-10 h-10 rounded-lg object-cover border border-gray-200"
            />
            <InfoText
              text={`${category.nameEn} | ${category.nameAr}`}
              className="!text-gray-800 font-semibold"
            />
          </div>

          <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={onEditCategory}
              className="w-8 h-8 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 flex items-center justify-center transition-colors"
            >
              <Edit2 size={16} variant="Bold" />
            </button>
            <button
              onClick={() => setIsDeleteModalOpen(true)}
              className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center transition-colors"
            >
              <Trash size={16} variant="Bold" />
            </button>
            <button
              onClick={onAddSubCategory}
              className="px-3 py-1.5 rounded-lg bg-green-50 hover:bg-green-100 text-light-success font-medium text-xs flex items-center gap-2 transition-colors"
            >
              <Add size={16} variant='Bold' /> {t('categories.addSubCategory')}
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-gray-100">
            {isLoadingSubCategories ? (
              <div className="py-6 flex items-center justify-center gap-2 text-sm text-gray-400">
                <svg className="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                {t('categories.loadingSubCategories', 'Loading...')}
              </div>
            ) : subCategories && subCategories.length > 0 ? (
              subCategories.map((subCat) => (
                <SubCategoryRow
                  key={subCat.id}
                  subCategory={subCat}
                  onEdit={() => onEditSubCategory(subCat)}
                />
              ))
            ) : (
              <div className="py-4 ps-10 text-sm text-gray-400">{t('categories.noSubCategories')}</div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <CustomModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={null}
        centered
        width={715}
        closable={false}
      >
        <div className="flex flex-col justify-center text-center gap-2 py-4">
          <div className="w-12 h-12 rounded-[14px] bg-[#FEF2F2] flex items-center justify-center self-center mb-2">
            <CloseSquare
              size={24}
              className="text-[#EF4444]"
              variant="Outline"
            />
          </div>

          <TitleText
            text={t('home.delete-item-title')}
            size="2xl"
            className="!font-bold !text-[#111928]"
          />

          <p className="text-gray-500 text-sm max-w-[420px] mx-auto leading-relaxed">
            {t('home.delete-item-desc')}
          </p>

          <div className="flex flex-col-reverse sm:flex-row items-center justify-center sm:justify-end gap-3 mt-6 w-full">
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
              className="w-full sm:w-auto rounded-xl px-8 py-2 font-semibold border border-gray-300 !bg-white !text-[#111928] hover:!bg-gray-50"
            >
              {t('home.cancel')}
            </Button>
            <Button
              variant="dark"
              loading={isDeleting}
              disabled={isDeleting}
              onClick={async () => {
                try {
                  await deleteOperatorCategory(category.id).unwrap();
                  setIsDeleteModalOpen(false);
                } catch (error: any) {
                  console.error('Failed to delete category:', error);
                  const errorMessage =
                    error?.data?.message ||
                    error?.data?.errors?.default ||
                    error?.data?.errors?.[0] ||
                    t('categories.categoryDeleteFailed', 'Failed to delete category');
                  message.error(errorMessage);
                }
              }}
              className="w-full sm:w-auto rounded-xl px-12 py-2 font-semibold !bg-[#D92D20] hover:!bg-[#B91C1C] !text-white !border-[#D92D20]"
            >
              {t('home.yes-delete')}
            </Button>
          </div>
        </div>
      </CustomModal>
    </>
  );
}

